import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Wallet, TrendingUp, ArrowUpRight, ShieldCheck, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { trashWallet, trashWalletHistory, trashCashout, cents, humanErr } from "@/lib/trashApi";

export default function TrashWallet() {
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [history, setHistory] = useState(null);
  const [showCashout, setShowCashout] = useState(false);

  const load = () => {
    trashWallet().then(setW).catch(() => {});
    trashWalletHistory().then((r) => setHistory(r.history)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return (
    <div className="py-16 text-center" data-testid="wallet-signin">
      <div className="font-black text-2xl mb-2">Sign in to see your wallet</div>
      <Link to="/login?next=/trashhunter/wallet" className="inline-flex items-center gap-1 bg-[#39FF14] text-black font-black text-xs uppercase tracking-widest px-5 py-2.5 rounded-full mt-3">Sign in</Link>
    </div>
  );
  if (!w) return <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;


  return (
    <div className="space-y-4" data-testid="trash-wallet">
      <JixxeoWalletCard coins={w.coins} />



      <section>
        <div className="font-black text-sm uppercase tracking-widest text-white/80 mb-2">Recent activity</div>
        {!history ? <div className="text-white/40 text-xs">Loading…</div> :
          history.length === 0 ? <div className="text-white/50 text-sm">No wallet activity yet. Scan some litter!</div> :
          <ul className="space-y-1" data-testid="wallet-history">
            {history.slice(0, 40).map((h) => (
              <li key={h.id} className="rounded-2xl border border-white/10 bg-black/40 p-3 flex items-center gap-2">
                <TrendingUp className={"w-3 h-3 " + (h.coins > 0 || h.cash_cents > 0 ? "text-[#39FF14]" : "text-[#F87171]")} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{h.note || h.kind.replace(/_/g, " ")}</div>
                  <div className="text-[10px] mono uppercase tracking-widest text-white/40">{h.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
                <div className="text-right shrink-0">
                  {!!h.coins && <div className={"text-xs font-black " + (h.coins > 0 ? "text-[#00F0FF]" : "text-[#F87171]")}>{h.coins > 0 ? "+" : ""}{h.coins} c</div>}
                </div>
              </li>
            ))}
          </ul>}
      </section>

      {showCashout && <CashoutModal wallet={w} onClose={() => setShowCashout(false)} onSubmitted={() => { setShowCashout(false); load(); }} />}
    </div>
  );
}

function CashoutModal({ wallet, onClose, onSubmitted }) {
  const [method, setMethod] = useState("paypal");
  const [handle, setHandle] = useState("");
  const [amountUsd, setAmountUsd] = useState((wallet.cashout_min_cents / 100).toFixed(2));
  const [busy, setBusy] = useState(false);
  const amountCents = Math.round(parseFloat(amountUsd || "0") * 100);
  const total = amountCents + wallet.cashout_fee_cents;
  const invalid = amountCents < wallet.cashout_min_cents || total > wallet.cash_cents;

  const submit = async () => {
    setBusy(true);
    try {
      await trashCashout({ method, handle: handle.trim(), amount_cents: amountCents });
      toast.success("Cash-out request submitted. Admin will review shortly.");
      onSubmitted();
    } catch (e) { toast.error(humanErr(e, "Cash-out failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4" data-testid="cashout-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl border-2 border-[#39FF14]/40 bg-[#001509] p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <div className="flex-1 font-black text-lg">Cash out</div>
          <button data-testid="cashout-close" onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-xs text-white/60">Balance: <b>{cents(wallet.cash_cents)}</b> · Min {cents(wallet.cashout_min_cents)} · Fee {cents(wallet.cashout_fee_cents)}</div>

        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-white/50 mb-1">Method</div>
          <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
            {["paypal","bank","crypto"].map((m) => (
              <button key={m} data-testid={`cashout-method-${m}`} onClick={() => setMethod(m)}
                className={"flex-1 py-2 text-xs uppercase tracking-widest font-black rounded-full " + (method === m ? "bg-[#39FF14] text-black" : "text-white/60")}>
                {m}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-white/50 mb-1">
            {method === "paypal" ? "PayPal email" : method === "bank" ? "Bank routing / account" : "Crypto wallet address"}
          </div>
          <input data-testid="cashout-handle" value={handle} onChange={(e) => setHandle(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-sm" />
        </div>

        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-white/50 mb-1">Amount (USD)</div>
          <input data-testid="cashout-amount" type="number" min={wallet.cashout_min_cents / 100} step="0.01"
            value={amountUsd} onChange={(e) => setAmountUsd(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-lg font-black tabular-nums" />
          <div className="text-[10px] mono uppercase tracking-widest text-white/50 mt-1">
            You receive: <b>{cents(amountCents)}</b> · Debits: <b>{cents(total)}</b> ({cents(wallet.cashout_fee_cents)} fee)
          </div>
        </div>

        <button data-testid="cashout-submit" onClick={submit} disabled={busy || invalid || !handle.trim()}
          className="w-full bg-[#39FF14] text-black font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Request cash-out"}
        </button>
        <div className="text-[10px] mono uppercase tracking-widest text-white/40 text-center">Payouts are reviewed by admin. This is a SIMULATED payout in preview mode.</div>
      </div>
    </div>
  );
}

function JixxeoWalletCard({ coins }) {
  const [pct, setPct] = useState(null);
  useEffect(() => { api.get("/earnings/wallet").then((r) => setPct(r.data.progress_pct)).catch(() => setPct(0)); }, []);
  return (
    <section className="rounded-3xl border-2 border-[#39FF14]/40 bg-gradient-to-br from-[#39FF14]/15 to-black/40 p-5" data-testid="trash-jixxeo-wallet">
      <div className="text-[10px] mono uppercase tracking-widest text-[#39FF14]">Jixxeo Wallet · progress to $5 cash-out</div>
      <div className="font-black text-5xl text-[#39FF14] tabular-nums" data-testid="wallet-progress">{pct === null ? "…" : `${pct}%`}</div>
      <div className="mt-2 h-3 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#39FF14]" style={{ width: `${pct || 0}%` }} /></div>
      <div className="text-xs text-white/60 mt-2">Verified cleanups add to your Jixxeo Wallet. Trash Hunter coins: <span className="text-[#00F0FF] font-black" data-testid="wallet-coins">{coins.toLocaleString()}</span></div>
      <Link to="/earn" data-testid="wallet-open-jixxeo" className="mt-4 inline-flex items-center gap-1 bg-[#39FF14] text-black font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full">Open Jixxeo Wallet</Link>
    </section>
  );
}
